import {ENTITLEMENT_KEYS} from '@packages/systems/billing/common';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ONLY_ON_PRIMARY_LOCALE} from './localization';

export const canAccessOptimizationSettings = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_CORE],
} as const);

export const canAccessOptimizationOrAnalytics = defineAbilityConfig([
  {
    entitlements: [ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_CORE],
  },
  {
    entitlements: [ENTITLEMENT_KEYS.ALLOW_ANALYZE_CORE],
  },
] as const);

export const canEditGoalElements = defineAbilityConfig([
  {
    siteModes: {design: true},
    branches: {main: true},
  },
] as const);

export const canAccessEnterpriseOptimizationSettings = defineAbilityConfig({
  entitlements: [
    ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_CORE,
    ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_ENTERPRISE_FEATURES,
  ],
} as const);

export const canAccessOfflineConversionTokens = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_OFFLINE_CONVERSION_TOKEN],
} as const);

export const canAccessOffSiteCustomMetric = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_OFF_SITE_CUSTOM_METRIC],
} as const);

export const canAccessAntiFlickerTimeout = defineAbilityConfig({
  entitlements: [
    ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_CORE,
    ENTITLEMENT_KEYS.ALLOW_OPTIMIZE_ENTERPRISE_FEATURES,
  ],
} as const);

/**
 * Grants access to the marketing integrations and the GA4/Segment/Mixpanel cards.
 *
 * ALLOW_SITE_ENTERPRISE_ANALYZE_CORE is a workspace-level entitlement (the workspace may
 * provision enterprise Analyze), so ALLOW_ANALYZE_CORE is what keeps this per-site —
 * dropping it would grant every site in the workspace.
 */
export const canAccessEnterpriseOptimizeAnalyzeIntegrations =
  defineAbilityConfig([
    {abilities: {canAccessEnterpriseOptimizationSettings: true}},
    {
      entitlements: [
        ENTITLEMENT_KEYS.ALLOW_ANALYZE_CORE,
        ENTITLEMENT_KEYS.ALLOW_SITE_ENTERPRISE_ANALYZE_CORE,
      ],
      featureFlags: {
        [FEATURE_FLAGS.ANALYZE_ENTERPRISE_INTEGRATIONS]: true,
      },
    },
  ] as const);

/**
 * Grants access to the Integrations panel. True when the user has either
 * enterprise Optimize/Analyze integrations (marketing + data export) or only
 * Analyze data exports (canViewDataExportsForAnalyze).
 * The panel content branches on these two abilities to show the appropriate layout.
 */
export const canAccessIntegrationsPanel = defineAbilityConfig([
  {abilities: {canAccessEnterpriseOptimizeAnalyzeIntegrations: true}},
  {abilities: {canViewDataExportsForAnalyze: true}},
] as const);

export const canManageTrackingSettings = defineAbilityConfig([
  {
    permission: ['manage', 'siteTrackingSettings'],
  },
] as const);

export const WHEN_NOT_IN_OPTIMIZE_MODE = {
  mode: 'out-of-optimize' as const,
} as const;

export const WHEN_IN_OPTIMIZE_MODE = {
  mode: 'in-optimize' as const,
} as const;

export const canSeeEditInVariationBlocker = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  optimization: WHEN_IN_OPTIMIZE_MODE,
} as const);

export const canManageOptimizationExperiences = defineAbilityConfig({
  permission: ['manage', 'siteOptimizeExperience'],
} as const);

export const canManageOptimizationVariations = defineAbilityConfig({
  permission: ['manage', 'siteOptimizeVariation'],
} as const);

export const canManageOptimizationVariationState = defineAbilityConfig({
  permission: ['manage', 'siteOptimizeVariationState'],
} as const);

export const canManageAnalyzeMetricEvents = defineAbilityConfig({
  permission: ['manage', 'siteOptimizeAnalyzeMetricEvent'],
} as const);

export const canManageOptimizationIntegrations = defineAbilityConfig({
  permission: ['manage', 'siteOptimizeIntegrations'],
} as const);

export const canAccessRedirectTests = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.OPTIMIZATION_REDIRECT_TESTS]: true,
  },
} as const);

export const canAccessLocaleAudience = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.OPTIMIZATION_LOCALE_AUDIENCE]: true,
  },
} as const);

export const canDeleteOptimizationWrappers = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.OPTIMIZATION_DELETE_OPTIMIZATION_WRAPPERS]: true,
  },
} as const);

export const canRelaunchAb = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.OPTIMIZATION_AB_RELAUNCHING]: true,
  },
} as const);

export const canAccessAiSuggestions = defineAbilityConfig({
  abilities: {canAccessAIFeatures: true},
  featureFlags: {
    [FEATURE_FLAGS.OPTIMIZATION_AI_ASSISTANT]: true,
  },
  siteModes: {design: true, edit: true},
  localization: ONLY_ON_PRIMARY_LOCALE,
} as const);

export const canAccessAiSuggestionsComponentMain = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.OPTIMIZATION_AI_ASSISTANT_COMPONENTS]: true,
  },
} as const);

export const canAccessAiInsights = defineAbilityConfig({
  abilities: {canAccessAIFeatures: true},
} as const);

export const canModifyStyleBlocksInOptimization = defineAbilityConfig({
  workflows: {optimization: true, canvas: true, optimizationRedirectTest: true},
  permission: ['modify', 'siteStyleBlock'],
} as const);

export const canModifyComponentsInOptimization = defineAbilityConfig({
  workflows: {optimization: true, canvas: true, optimizationRedirectTest: true},
  permission: ['modify', 'siteSymbol'],
} as const);

export const canModifyComponentInstanceInOptimization = defineAbilityConfig([
  {
    siteModes: {design: true},
    workflows: {
      optimization: true,
      canvas: true,
      optimizationRedirectTest: true,
    },
  },
  {
    abilities: {isInPageBuildMode: true},
    workflows: {
      optimization: true,
      canvas: true,
      optimizationRedirectTest: true,
    },
  },
] as const);
